import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { MONDES } from "../lib/pulaar";

export default function Univers() {
  const atelierWide = "/brand/pulaar-atelier-geste-tissage-main.jpg";
  const { t } = useTranslation();

  return (
    <main
      data-testid="univers-page"
      className="relative pt-32 md:pt-40 pb-24 bg-[#FBF8EF]"
    >
      <SEO
        title="Archives"
        description="Pulaar Archives — trois chapitres : Maabube (la main qui transmet), Dammeel (le seuil), Ceddo (le cavalier). Mémoire vivante du peuple peul."
        path="/archives"
      />
      {/* HERO */}
      <section
        data-testid="archives-hero"
        className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 mb-24 md:mb-32"
      >
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
            {t("archives.eyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-[40px] md:text-[68px] lg:text-[88px] leading-[0.95] tracking-[-0.02em] text-[#121212] max-w-[14ch]">
            {t("archives.titleA")}<span className="italic font-light">{t("archives.titleEm")}</span>{t("archives.titleC")}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-2xl text-base md:text-lg leading-[1.85] text-[#4A4641]">
            {t("archives.intro")}
          </p>
        </Reveal>
      </section>

      {/* CHAPITRES — 3 mondes */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 space-y-24 md:space-y-36">
        {MONDES.map((m, idx) => {
          const subtitle = t(`mondes.${m.slug}.subtitle`, { defaultValue: m.subtitle });
          const teaser = t(`mondes.${m.slug}.teaser`, { defaultValue: m.teaser });
          return (
            <Reveal key={m.slug}>
              <article data-testid={`univers-monde-${m.slug}`}>
                <div
                  className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${
                    idx % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="md:col-span-7">
                    <Link to={`/archives/${m.slug}`} className="group block overflow-hidden">
                      <img
                        src={m.archiveCover || m.cover}
                        alt={m.name}
                        loading="lazy"
                        className="w-full aspect-[4/5] md:aspect-[16/11] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
                      />
                    </Link>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
                      {t("archives.chapter", { symbol: m.symbol })}
                    </p>
                    <h2 className="font-serif text-4xl md:text-6xl tracking-[-0.02em] text-[#121212] leading-[1.02]">
                      {m.name}
                    </h2>
                    <p className="mt-4 font-serif italic text-lg md:text-xl text-[#5C4033]">
                      {subtitle}
                    </p>
                    <p className="mt-8 text-base leading-[1.85] text-[#4A4641] max-w-md">
                      {teaser}
                    </p>
                    <Link
                      to={`/archives/${m.slug}`}
                      data-testid={`univers-monde-link-${m.slug}`}
                      className="mt-12 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#121212] group"
                    >
                      <span className="link-underline">{t("archives.readStory")}</span>
                      <ArrowRight
                        size={14}
                        strokeWidth={1.2}
                        className="transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      {/* ATELIER */}
      <section
        data-testid="archives-atelier-intro"
        className="mt-36 md:mt-48 border-t border-[#E5DFD3] pt-20 md:pt-28"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
              {t("archives.atelierEyebrow")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-[40px] md:text-[68px] lg:text-[88px] leading-[0.95] tracking-[-0.02em] text-[#121212] max-w-[14ch]">
              {t("archives.atelierTitleA")}<span className="italic font-light">{t("archives.atelierTitleEm")}</span>{t("archives.atelierTitleC")}
            </h2>
          </Reveal>
        </div>

        <div className="relative h-[55vh] md:h-[80vh] overflow-hidden mt-16 md:mt-24">
          <img
            src={atelierWide}
            alt="Atelier Pulaar"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute bottom-5 left-5 md:bottom-10 md:left-12 right-5 md:right-12 flex items-end justify-between text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase">
            <span>{t("archives.atelierBadge")}</span>
            <span className="hidden md:block">{t("archives.citiesBadge")}</span>
          </div>
        </div>
      </section>

      {/* TRANSMISSION */}
      <section
        data-testid="archives-transmission"
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="/heritage/pulaar-transmission-artisanat-textile.jpg"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/45 to-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-32 md:py-48 text-[#FBF8EF]">
          <Reveal className="max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#E5DFD3] mb-6">
              {t("archives.transmissionEyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] text-balance">
              {t("archives.transmissionTitleA")}<span className="italic font-light">{t("archives.transmissionTitleEm")}</span>{t("archives.transmissionTitleC")}
            </h2>
            <p className="mt-8 text-base md:text-lg leading-[1.9] text-[#E5DFD3] max-w-xl">
              {t("archives.transmissionText")}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
